import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Shield, Zap, Users, Award, ArrowRight, Building2, Target, Briefcase, MapPin, Clock, UserPlus, Mail, Phone, Send, MessageCircle } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import toast from 'react-hot-toast'

export const CompanyPage: React.FC = () => {
  const location = useLocation()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  })

  useEffect(() => {
    // Scroll to section if hash is present
    if (location.hash) {
      const element = document.querySelector(location.hash)
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    }
  }, [location.hash])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    toast.success('Thank you for your message! We\'ll get back to you soon.')
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' })
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-6xl mb-6">
              About Open1031
            </h1>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              We're revolutionizing the 1031 exchange process by combining cutting-edge technology 
              with deep domain expertise to deliver the most comprehensive exchange solution available.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-foreground mb-6">
                  Our Mission
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Open1031 was founded with a simple goal: to make 1031 exchanges accessible, efficient,
                  and stress-free for real estate investors. We believe that technology should handle the
                  complexity, so you can focus on what matters most—growing your portfolio.
                </p>
                <p className="text-lg text-muted-foreground">
                  Our platform automates the entire 1031 exchange lifecycle, from initial setup through 
                  final IRS reporting, ensuring compliance at every step while dramatically reducing the 
                  time and effort required.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary/10 to-primary/20 p-8 rounded-2xl border border-primary/20">
                <div className="text-center">
                  <Target className="h-16 w-16 text-primary mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    Our Vision
                  </h3>
                  <p className="text-muted-foreground">
                    To become the leading platform for 1031 exchanges, empowering investors to maximize 
                    their wealth through seamless, compliant, and efficient property exchanges.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-foreground text-center mb-12">
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-card p-6 rounded-2xl shadow-lg border border-border hover:shadow-xl transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Security First
              </h3>
              <p className="text-muted-foreground">
                Your data and transactions are protected with bank-level security and encryption.
              </p>
            </div>

            <div className="bg-card p-6 rounded-2xl shadow-lg border border-border hover:shadow-xl transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Innovation
              </h3>
              <p className="text-muted-foreground">
                We continuously innovate to provide the best tools and features for our users.
              </p>
            </div>

            <div className="bg-card p-6 rounded-2xl shadow-lg border border-border hover:shadow-xl transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Customer Focus
              </h3>
              <p className="text-muted-foreground">
                Your success is our success. We're committed to providing exceptional support.
              </p>
            </div>

            <div className="bg-card p-6 rounded-2xl shadow-lg border border-border hover:shadow-xl transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Award className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Excellence
              </h3>
              <p className="text-muted-foreground">
                We maintain the highest standards in everything we do, from code to customer service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-20 relative z-10 bg-muted/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold tracking-tight text-foreground text-center mb-4">
              Our Founders
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-12">
              Meet the visionary leaders who founded Open1031 and are transforming the 1031 exchange industry.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {/* Founder 1 */}
              <div className="bg-card p-6 rounded-2xl shadow-lg border border-border text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/80 mx-auto mb-4 flex items-center justify-center">
                  <Users className="h-12 w-12 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Founder Name</h3>
                <p className="text-sm text-primary mb-2">CEO & Co-Founder</p>
                <p className="text-sm text-muted-foreground">
                  Real estate industry veteran with 20+ years of experience
                </p>
              </div>

              {/* Founder 2 */}
              <div className="bg-card p-6 rounded-2xl shadow-lg border border-border text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/80 mx-auto mb-4 flex items-center justify-center">
                  <Shield className="h-12 w-12 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Founder Name</h3>
                <p className="text-sm text-primary mb-2">CTO & Co-Founder</p>
                <p className="text-sm text-muted-foreground">
                  Technology leader specializing in fintech and compliance
                </p>
              </div>

              {/* Founder 3 */}
              <div className="bg-card p-6 rounded-2xl shadow-lg border border-border text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/80 mx-auto mb-4 flex items-center justify-center">
                  <Award className="h-12 w-12 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Founder Name</h3>
                <p className="text-sm text-primary mb-2">CFO & Co-Founder</p>
                <p className="text-sm text-muted-foreground">
                  Financial expert with deep expertise in tax and accounting
                </p>
              </div>

              {/* Founder 4 */}
              <div className="bg-card p-6 rounded-2xl shadow-lg border border-border text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/80 mx-auto mb-4 flex items-center justify-center">
                  <Building2 className="h-12 w-12 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Founder Name</h3>
                <p className="text-sm text-primary mb-2">COO & Co-Founder</p>
                <p className="text-sm text-muted-foreground">
                  Operations leader with extensive real estate transaction experience
                </p>
              </div>

              {/* Founder 5 */}
              <div className="bg-card p-6 rounded-2xl shadow-lg border border-border text-center">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary/80 mx-auto mb-4 flex items-center justify-center">
                  <Zap className="h-12 w-12 text-primary-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-1">Founder Name</h3>
                <p className="text-sm text-primary mb-2">CPO & Co-Founder</p>
                <p className="text-sm text-muted-foreground">
                  Product visionary focused on user experience and innovation
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold tracking-tight text-foreground mb-6">
              Built by Experts, For Investors
            </h2>
            <p className="text-lg text-muted-foreground mb-12">
              Our team combines decades of experience in real estate, tax law, and technology to create
              a platform that truly understands the needs of 1031 exchange participants.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-card p-6 rounded-2xl shadow-lg border border-border">
                <Building2 className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-foreground mb-2">Real Estate Experts</h3>
                <p className="text-sm text-muted-foreground">
                  Team members with extensive backgrounds in commercial and investment real estate
                </p>
              </div>
              <div className="bg-card p-6 rounded-2xl shadow-lg border border-border">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-foreground mb-2">Tax Compliance</h3>
                <p className="text-sm text-muted-foreground">
                  Certified tax professionals ensuring full IRS compliance and accuracy
                </p>
              </div>
              <div className="bg-card p-6 rounded-2xl shadow-lg border border-border">
                <Zap className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="font-semibold text-foreground mb-2">Tech Innovation</h3>
                <p className="text-sm text-muted-foreground">
                  Engineers building cutting-edge solutions with modern technology
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section id="careers" className="py-20 relative z-10 bg-muted/50 scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Briefcase className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-4">
              Join Our Team
            </h2>
            <p className="text-lg text-muted-foreground">
              Help us revolutionize 1031 exchanges. We're building the future of real estate investing.
            </p>
          </div>

          {/* Open Positions */}
          <div className="max-w-4xl mx-auto space-y-6 mb-12">
            {/* Job Listing 1 */}
            <div className="bg-card p-6 rounded-xl shadow-lg border border-border hover:shadow-xl transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Senior Full-Stack Engineer
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      Remote / San Francisco
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      Full-time
                    </span>
                  </div>
                </div>
                <a href="#contact">
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                    Apply Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
              <p className="text-muted-foreground">
                We're looking for an experienced full-stack engineer to help build our next-generation 1031 exchange platform.
              </p>
            </div>

            {/* Job Listing 2 */}
            <div className="bg-card p-6 rounded-xl shadow-lg border border-border hover:shadow-xl transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Tax Compliance Specialist
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      Remote / New York
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      Full-time
                    </span>
                  </div>
                </div>
                <a href="#contact">
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                    Apply Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
              <p className="text-muted-foreground">
                Join our compliance team to ensure all exchanges meet IRS regulations and requirements.
              </p>
            </div>

            {/* Job Listing 3 */}
            <div className="bg-card p-6 rounded-xl shadow-lg border border-border hover:shadow-xl transition-shadow">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Customer Success Manager
                  </h3>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center">
                      <MapPin className="h-4 w-4 mr-1" />
                      Remote
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      Full-time
                    </span>
                  </div>
                </div>
                <a href="#contact">
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                    Apply Now
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
              <p className="text-muted-foreground">
                Help our customers succeed with their 1031 exchanges through exceptional support and guidance.
              </p>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-foreground text-center mb-8">
              Why Work at Open1031?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  Competitive Compensation
                </h4>
                <p className="text-muted-foreground">
                  We offer competitive salaries, equity, and comprehensive benefits packages.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  Remote-Friendly
                </h4>
                <p className="text-muted-foreground">
                  Work from anywhere with flexible hours and a supportive remote culture.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  Growth Opportunities
                </h4>
                <p className="text-muted-foreground">
                  Advance your career with learning opportunities and mentorship programs.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  Impactful Work
                </h4>
                <p className="text-muted-foreground">
                  Make a real difference in how people manage their real estate investments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section id="partners" className="py-20 relative z-10 scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <UserPlus className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-4">
              Partner With Us
            </h2>
            <p className="text-lg text-muted-foreground">
              Join our partner network and help your clients complete successful 1031 exchanges.
            </p>
          </div>

          {/* Partner Types */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Real Estate Brokers */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border text-center">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Real Estate Brokers
              </h3>
              <p className="text-muted-foreground mb-6">
                Offer your clients a seamless 1031 exchange experience with our integrated platform.
              </p>
              <a href="#contact">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Become a Partner
                </Button>
              </a>
            </div>

            {/* Financial Advisors */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border text-center">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                <UserPlus className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Financial Advisors
              </h3>
              <p className="text-muted-foreground mb-6">
                Provide comprehensive tax planning services with our 1031 exchange solutions.
              </p>
              <a href="#contact">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Become a Partner
                </Button>
              </a>
            </div>

            {/* Technology Partners */}
            <div className="bg-card p-8 rounded-2xl shadow-lg border border-border text-center">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Technology Partners
              </h3>
              <p className="text-muted-foreground mb-6">
                Integrate with our API to build powerful 1031 exchange solutions for your platform.
              </p>
              <a href="#contact">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Become a Partner
                </Button>
              </a>
            </div>
          </div>

          {/* Partner Benefits */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-foreground text-center mb-8">
              Partner Benefits
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  Revenue Sharing
                </h4>
                <p className="text-muted-foreground">
                  Earn competitive commissions for referrals and successful exchanges.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  Dedicated Support
                </h4>
                <p className="text-muted-foreground">
                  Access to a dedicated partner success manager and priority support.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  Marketing Resources
                </h4>
                <p className="text-muted-foreground">
                  Co-marketing opportunities and materials to help promote your services.
                </p>
              </div>
              <div className="bg-card p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-foreground mb-2">
                  Training & Certification
                </h4>
                <p className="text-muted-foreground">
                  Comprehensive training programs to help you become a 1031 exchange expert.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 relative z-10 bg-muted/50 scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-4">
              Get in Touch
            </h2>
            <p className="text-lg text-muted-foreground">
              Have questions? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-8">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Email</h4>
                      <p className="text-muted-foreground">support@open1031.com</p>
                      <p className="text-muted-foreground">sales@open1031.com</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Phone</h4>
                      <p className="text-muted-foreground">1-800-OPEN-1031</p>
                      <p className="text-muted-foreground">Mon-Fri 9am-5pm PST</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Address</h4>
                      <p className="text-muted-foreground">
                        123 Exchange Street<br />
                        San Francisco, CA 94105<br />
                        United States
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-12 p-6 bg-primary/10 rounded-2xl border border-primary/20">
                  <div className="flex items-start space-x-3">
                    <MessageCircle className="h-6 w-6 text-primary mt-1" />
                    <div>
                      <h4 className="font-semibold text-foreground mb-2">
                        Prefer to Chat?
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Our AI assistant Neela is available 24/7 to answer your questions and help you get started.
                      </p>
                      <Link to="/">
                        <Button variant="outline" size="sm">
                          Start Chat
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h3 className="text-2xl font-bold text-foreground mb-8">
                  Send us a Message
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Name *
                      </label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Email *
                      </label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Phone
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 555-5555"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Subject *
                    </label>
                    <Input
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="What can we help you with?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Message *
                    </label>
                    <textarea
                      required
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us more about your inquiry..."
                      className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    <Send className="mr-2 h-4 w-4" />
                    Send Message
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

